import "./styles.css";
import { createIcons, icons } from "lucide";

type Owner = "player" | "enemy";
type Terrain = "clear" | "rough" | "road" | "water" | "rock" | "ore";
type MapMode = "small" | "large";
type UnitKind = "scout" | "medium" | "artillery" | "harvester" | "hero";
type StructureKind = "hq" | "factory" | "refinery" | "power" | "turret";
type OrderKind = "idle" | "move" | "attackMove" | "attack" | "guard" | "harvest";
type CommandMode = "attackMove" | "guard" | "hero" | null;

const TILE_W = 92;
const TILE_H = 46;
const HALF_W = TILE_W / 2;
const HALF_H = TILE_H / 2;
const BASE_URL = import.meta.env.BASE_URL;
const ASSET_URL = `${BASE_URL}assets/kenney/`;
const SFX_URL = `${BASE_URL}sfx/`;
const SFX_NAMES = ["select", "move", "attack", "build", "explosion", "hero", "alert"] as const;

interface Vec2 {
  x: number;
  y: number;
}

interface Cell {
  terrain: Terrain;
  ore: number;
  explored: boolean;
  visible: boolean;
}

interface UnitDef {
  name: string;
  role: string;
  cost: number;
  time: number;
  hp: number;
  speed: number;
  range: number;
  damage: number;
  reload: number;
  reveal: number;
  body?: string;
  turret?: string;
  shell?: string;
  icon: string;
  hero?: boolean;
  harvester?: boolean;
}

interface StructureDef {
  name: string;
  w: number;
  h: number;
  hp: number;
  height: number;
  top: string;
  side: string;
  accent: string;
  reveal: number;
  turret?: boolean;
  power: number;
}

interface Unit {
  id: number;
  kind: UnitKind;
  owner: Owner;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  facing: number;
  turretFacing: number;
  order: OrderKind;
  path: Vec2[];
  targetId?: number;
  guard?: Vec2;
  cooldown: number;
  heroCooldown: number;
  empTimer: number;
  ore: number;
  selected: boolean;
  veteran: number;
}

interface Structure {
  id: number;
  kind: StructureKind;
  owner: Owner;
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  cooldown: number;
}

interface Projectile {
  id: number;
  owner: Owner;
  x: number;
  y: number;
  tx: number;
  ty: number;
  targetId?: number;
  damage: number;
  radius: number;
  speed: number;
  color: string;
}

interface Explosion {
  x: number;
  y: number;
  age: number;
  duration: number;
  radius: number;
  color: string;
}

interface BuildOrder {
  kind: UnitKind;
  progress: number;
  duration: number;
}

interface Decor {
  x: number;
  y: number;
  image: string;
  scale: number;
}

interface PointerState {
  x: number;
  y: number;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  button: number;
  pointerType: string;
  startedOnUnit: boolean;
  panning: boolean;
  selecting: boolean;
}

const UNIT_DEFS: Record<UnitKind, UnitDef> = {
  scout: {
    name: "Scout Tank",
    role: "Fast armor",
    cost: 420,
    time: 5.5,
    hp: 86,
    speed: 2.7,
    range: 4.4,
    damage: 14,
    reload: 0.75,
    reveal: 6,
    body: "tankBody_green",
    turret: "tankGreen_barrel1",
    shell: "bulletGreen1",
    icon: "gauge"
  },
  medium: {
    name: "Battle Tank",
    role: "Main armor",
    cost: 780,
    time: 8.5,
    hp: 152,
    speed: 1.9,
    range: 5.1,
    damage: 27,
    reload: 1.25,
    reveal: 6,
    body: "tankBody_blue",
    turret: "tankBlue_barrel2",
    shell: "bulletBlue2",
    icon: "shield"
  },
  artillery: {
    name: "Siege Carrier",
    role: "Long range",
    cost: 980,
    time: 11,
    hp: 112,
    speed: 1.45,
    range: 7.5,
    damage: 38,
    reload: 1.9,
    reveal: 7,
    body: "tankBody_sand",
    turret: "specialBarrel6",
    shell: "bulletSand3",
    icon: "target"
  },
  harvester: {
    name: "Harvester",
    role: "Resource collector",
    cost: 640,
    time: 8,
    hp: 132,
    speed: 1.55,
    range: 0,
    damage: 0,
    reload: 1,
    reveal: 5,
    body: "tankBody_darkLarge",
    turret: "crateMetal",
    shell: "bulletDark1",
    icon: "factory",
    harvester: true
  },
  hero: {
    name: "Ghost Captain",
    role: "Hero infantry",
    cost: 0,
    time: 0,
    hp: 118,
    speed: 2.25,
    range: 5.7,
    damage: 22,
    reload: 0.85,
    reveal: 8,
    shell: "bulletGreen3",
    icon: "sparkles",
    hero: true
  }
};

const ENEMY_SKINS: Partial<Record<UnitKind, Pick<UnitDef, "body" | "turret" | "shell">>> = {
  scout: { body: "tankBody_red", turret: "tankRed_barrel1", shell: "bulletRed1" },
  medium: { body: "tankBody_red", turret: "tankRed_barrel2", shell: "bulletRed2" },
  artillery: { body: "tankBody_bigRed", turret: "specialBarrel7", shell: "bulletRed3" },
  harvester: { body: "tankBody_dark", turret: "crateWood", shell: "bulletDark1" },
  hero: { shell: "bulletRed3" }
};

const STRUCTURE_DEFS: Record<StructureKind, StructureDef> = {
  hq: {
    name: "Command Hub",
    w: 3,
    h: 3,
    hp: 520,
    height: 48,
    top: "#53646a",
    side: "#2d3a3f",
    accent: "#f5db8c",
    reveal: 9,
    power: 0
  },
  factory: {
    name: "War Factory",
    w: 4,
    h: 3,
    hp: 420,
    height: 36,
    top: "#5b6156",
    side: "#303b33",
    accent: "#87d9df",
    reveal: 7,
    power: -20
  },
  refinery: {
    name: "Refinery",
    w: 3,
    h: 2,
    hp: 360,
    height: 34,
    top: "#696246",
    side: "#3c392d",
    accent: "#a7d454",
    reveal: 7,
    power: -10
  },
  power: {
    name: "Power Plant",
    w: 2,
    h: 2,
    hp: 280,
    height: 42,
    top: "#5e6c64",
    side: "#32403d",
    accent: "#f4c84d",
    reveal: 6,
    power: 75
  },
  turret: {
    name: "Gun Turret",
    w: 1,
    h: 1,
    hp: 210,
    height: 26,
    top: "#576060",
    side: "#323838",
    accent: "#e26457",
    reveal: 6,
    turret: true,
    power: -15
  }
};

const TERRAIN_COLORS: Record<Terrain, string> = {
  clear: "#2f5134",
  rough: "#4b5148",
  road: "#665540",
  water: "#315d76",
  rock: "#353b3b",
  ore: "#536c35"
};

const TERRAIN_SPEED: Record<Terrain, number> = {
  clear: 1,
  rough: 0.82,
  road: 1.24,
  water: 0,
  rock: 0,
  ore: 0.85
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function hash01(x: number, y: number, seed: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453123;
  return n - Math.floor(n);
}

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${url}`));
    img.src = url;
  });
}

class MinHeap<T> {
  private items: { key: number; value: T }[] = [];

  push(key: number, value: T): void {
    this.items.push({ key, value });
    this.bubbleUp(this.items.length - 1);
  }

  pop(): T | undefined {
    const first = this.items[0];
    const last = this.items.pop();
    if (!first || !last) return undefined;
    if (this.items.length > 0) {
      this.items[0] = last;
      this.bubbleDown(0);
    }
    return first.value;
  }

  get size(): number {
    return this.items.length;
  }

  private bubbleUp(index: number): void {
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.items[parent].key <= this.items[index].key) return;
      [this.items[parent], this.items[index]] = [this.items[index], this.items[parent]];
      index = parent;
    }
  }

  private bubbleDown(index: number): void {
    while (true) {
      const left = index * 2 + 1;
      const right = left + 1;
      let smallest = index;
      if (left < this.items.length && this.items[left].key < this.items[smallest].key) smallest = left;
      if (right < this.items.length && this.items[right].key < this.items[smallest].key) smallest = right;
      if (smallest === index) return;
      [this.items[smallest], this.items[index]] = [this.items[index], this.items[smallest]];
      index = smallest;
    }
  }
}

class GameAudio {
  private ctx: AudioContext | null = null;
  private buffers = new Map<string, AudioBuffer>();
  private unlocked = false;

  async init(): Promise<void> {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;
    this.ctx = new AudioContextCtor();
    await Promise.all(SFX_NAMES.map((name) => this.load(name)));
  }

  async unlock(): Promise<void> {
    if (!this.ctx) return;
    if (this.ctx.state === "suspended") await this.ctx.resume();
    this.unlocked = true;
  }

  play(name: (typeof SFX_NAMES)[number], volume = 0.7): void {
    if (!this.ctx) return;
    const buffer = this.buffers.get(name);
    if (buffer) {
      const source = this.ctx.createBufferSource();
      const gain = this.ctx.createGain();
      gain.gain.value = volume;
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(this.ctx.destination);
      source.start();
      return;
    }

    if (this.unlocked) this.synthetic(name, volume);
  }

  private async load(name: string): Promise<void> {
    if (!this.ctx) return;

    for (const ext of ["mp3", "wav"]) {
      try {
        const response = await fetch(`${SFX_URL}${name}.${ext}`, { cache: "force-cache" });
        if (!response.ok) continue;
        const arrayBuffer = await response.arrayBuffer();
        const buffer = await this.ctx.decodeAudioData(arrayBuffer.slice(0));
        this.buffers.set(name, buffer);
        return;
      } catch {
        // Procedural fallback covers missing or unsupported files.
      }
    }
  }

  private synthetic(name: string, volume: number): void {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(volume * 0.18, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    gain.connect(this.ctx.destination);

    const osc = this.ctx.createOscillator();
    osc.type = name === "alert" ? "sawtooth" : "triangle";
    osc.frequency.setValueAtTime(name === "explosion" ? 80 : name === "build" ? 360 : 720, now);
    osc.frequency.exponentialRampToValueAtTime(name === "explosion" ? 38 : 420, now + 0.17);
    osc.connect(gain);
    osc.start(now);
    osc.stop(now + 0.2);
  }
}

class RtsGame {
  private canvas = document.querySelector<HTMLCanvasElement>("#gameCanvas")!;
  private ctx = this.canvas.getContext("2d", { alpha: false })!;
  private minimap = document.querySelector<HTMLCanvasElement>("#minimapCanvas")!;
  private miniCtx = this.minimap.getContext("2d", { alpha: false })!;
  private loading = document.querySelector<HTMLElement>("#loadingOverlay")!;
  private creditsEl = document.querySelector<HTMLElement>("#creditsValue")!;
  private powerEl = document.querySelector<HTMLElement>("#powerValue")!;
  private timeEl = document.querySelector<HTMLElement>("#timeValue")!;
  private objectiveEl = document.querySelector<HTMLElement>("#objectiveText")!;
  private productionList = document.querySelector<HTMLElement>("#productionList")!;
  private queueState = document.querySelector<HTMLElement>("#queueState")!;
  private selectionSummary = document.querySelector<HTMLElement>("#selectionSummary")!;
  private unitCards = document.querySelector<HTMLElement>("#unitCards")!;
  private toastLog = document.querySelector<HTMLElement>("#toastLog")!;
  private heroButton = document.querySelector<HTMLButtonElement>("#heroButton")!;
  private heroButtonText = document.querySelector<HTMLElement>("#heroButtonText")!;

  private width = 1280;
  private height = 720;
  private dpr = 1;
  private mapSize = 42;
  private mode: MapMode = "large";
  private map: Cell[][] = [];
  private decor: Decor[] = [];
  private assets = new Map<string, HTMLImageElement>();
  private audio = new GameAudio();

  private units: Unit[] = [];
  private structures: Structure[] = [];
  private projectiles: Projectile[] = [];
  private explosions: Explosion[] = [];
  private selected = new Set<number>();
  private buildQueue: BuildOrder[] = [];

  private nextId = 1;
  private camera = { x: 0, y: 0, zoom: 1.05 };
  private keys = new Set<string>();
  private pointers = new Map<number, PointerState>();
  private pinchStart: { distance: number; zoom: number; center: Vec2; camera: Vec2 } | null = null;
  private mouse = { x: 0, y: 0, inside: false };
  private selectionBox: { start: Vec2; end: Vec2 } | null = null;
  private commandMode: CommandMode = null;
  private status: "playing" | "victory" | "defeat" = "playing";
  private credits = 2800;
  private elapsed = 0;
  private lastFrame = performance.now();
  private enemyBuildTimer = 4;
  private enemyWaveTimer = 18;
  private hudTimer = 0;
  private raf = 0;

  constructor() {
    createIcons({ icons });
    this.attachEvents();
    void this.start();
  }

  private async start(): Promise<void> {
    this.resize();
    await Promise.all([this.loadAssets(), this.audio.init()]);
    this.newScenario("large");
    this.loading.classList.add("hidden");
    this.lastFrame = performance.now();
    this.raf = requestAnimationFrame((time) => this.frame(time));
  }

  private async loadAssets(): Promise<void> {
    const names = new Set<string>();
    for (const def of Object.values(UNIT_DEFS)) {
      if (def.body) names.add(def.body);
      if (def.turret) names.add(def.turret);
      if (def.shell) names.add(def.shell);
    }
    for (const skin of Object.values(ENEMY_SKINS)) {
      if (skin?.body) names.add(skin.body);
      if (skin?.turret) names.add(skin.turret);
      if (skin?.shell) names.add(skin.shell);
    }
    [
      "treeGreen_large",
      "treeGreen_small",
      "treeBrown_large",
      "treeBrown_small",
      "crateWood",
      "crateMetal",
      "barrelRed_top",
      "barrelRust_top",
      "explosion1",
      "explosion2",
      "explosion3",
      "explosion4",
      "explosion5",
      "shotLarge",
      "shotOrange",
      "oilSpill_small"
    ].forEach((name) => names.add(name));

    await Promise.all(
      [...names].map(async (name) => {
        const image = await loadImage(`${ASSET_URL}${name}.png`);
        this.assets.set(name, image);
      })
    );
  }

  private attachEvents(): void {
    window.addEventListener("resize", () => this.resize());
    window.addEventListener("keydown", (event) => this.handleKey(event, true));
    window.addEventListener("keyup", (event) => this.handleKey(event, false));
    window.addEventListener("blur", () => this.keys.clear());

    this.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    this.canvas.addEventListener("pointerenter", () => (this.mouse.inside = true));
    this.canvas.addEventListener("pointerleave", () => (this.mouse.inside = false));
    this.canvas.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    this.canvas.addEventListener("pointermove", (event) => this.onPointerMove(event));
    this.canvas.addEventListener("pointerup", (event) => this.onPointerUp(event));
    this.canvas.addEventListener("pointercancel", (event) => this.onPointerCancel(event));
    this.canvas.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        if (event.ctrlKey || event.metaKey) {
          this.zoomAt(event.clientX, event.clientY, 1 - event.deltaY * 0.0014);
        } else {
          this.camera.x += event.deltaX / this.camera.zoom;
          this.camera.y += event.deltaY / this.camera.zoom;
          this.clampCamera();
        }
      },
      { passive: false }
    );

    this.minimap.addEventListener("pointerdown", (event) => this.onMinimapPointer(event));
    this.minimap.addEventListener("pointermove", (event) => {
      if (event.buttons) this.onMinimapPointer(event);
    });

    document.querySelector<HTMLButtonElement>("#zoomInButton")!.addEventListener("click", () => this.zoomAt(this.width / 2, this.height / 2, 1.16));
    document.querySelector<HTMLButtonElement>("#zoomOutButton")!.addEventListener("click", () => this.zoomAt(this.width / 2, this.height / 2, 0.86));
    document.querySelector<HTMLButtonElement>("#centerButton")!.addEventListener("click", () => this.centerOnPlayerBase());
    document.querySelector<HTMLButtonElement>("#mapModeButton")!.addEventListener("click", () => {
      this.newScenario(this.mode === "large" ? "small" : "large");
      this.toast(`Map: ${this.mode === "large" ? "Frontier" : "Skirmish"}`);
    });

    document.querySelector<HTMLButtonElement>("#attackMoveButton")!.addEventListener("click", () => this.toggleMode("attackMove"));
    document.querySelector<HTMLButtonElement>("#guardButton")!.addEventListener("click", () => this.toggleMode("guard"));
    document.querySelector<HTMLButtonElement>("#scatterButton")!.addEventListener("click", () => this.scatterSelected());
    document.querySelector<HTMLButtonElement>("#stopButton")!.addEventListener("click", () => this.stopSelected());
    this.heroButton.addEventListener("click", () => this.toggleMode("hero"));
  }

  private resize(): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    const rect = this.canvas.getBoundingClientRect();
    this.width = Math.max(1, rect.width);
    this.height = Math.max(1, rect.height);
    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

    const miniRect = this.minimap.getBoundingClientRect();
    this.minimap.width = Math.floor(Math.max(1, miniRect.width) * this.dpr);
    this.minimap.height = Math.floor(Math.max(1, miniRect.height) * this.dpr);
    this.miniCtx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.clampCamera();
  }

  private handleKey(event: KeyboardEvent, down: boolean): void {
    if (down) {
      if (event.key === "a" || event.key === "A") this.toggleMode("attackMove");
      if (event.key === "g" || event.key === "G") this.toggleMode("guard");
      if (event.key === "h" || event.key === "H") this.toggleMode("hero");
      if (event.key === "s" || event.key === "S") this.stopSelected();
      if (event.key === " ") this.centerOnSelectionOrBase();
      if (event.key === "+" || event.key === "=") this.zoomAt(this.width / 2, this.height / 2, 1.16);
      if (event.key === "-" || event.key === "_") this.zoomAt(this.width / 2, this.height / 2, 0.86);
    }

    const key = event.key.toLowerCase();
    if (["arrowup", "arrowdown", "arrowleft", "arrowright", "w", "a", "s", "d"].includes(key)) {
      event.preventDefault();
      if (down) this.keys.add(key);
      else this.keys.delete(key);
    }
  }

  private newScenario(mode: MapMode): void {
    this.mode = mode;
    this.mapSize = mode === "small" ? 22 : 44;
    this.nextId = 1;
    this.units = [];
    this.structures = [];
    this.projectiles = [];
    this.explosions = [];
    this.decor = [];
    this.selected.clear();
    this.buildQueue = [];
    this.commandMode = null;
    this.status = "playing";
    this.elapsed = 0;
    this.credits = mode === "small" ? 2100 : 2800;
    this.enemyBuildTimer = mode === "small" ? 7 : 4;
    this.enemyWaveTimer = mode === "small" ? 20 : 18;
    this.generateMap();
    this.spawnBases();
    this.updateVisibility();
    this.centerOnPlayerBase();
    this.renderProduction();
    this.updateHud();
  }

  private generateMap(): void {
    const seed = this.mode === "small" ? 1287 : 2026;
    this.map = Array.from({ length: this.mapSize }, (_, y) =>
      Array.from({ length: this.mapSize }, (_, x) => {
        const ripple = Math.sin((x + seed) * 0.31) + Math.cos((y - seed) * 0.27);
        const band = Math.abs(y - (this.mapSize * 0.58 - x * 0.18));
        let terrain: Terrain = ripple > 1.15 ? "rough" : "clear";
        if (band < 0.75 && x > this.mapSize * 0.18 && x < this.mapSize * 0.78) terrain = "water";
        if (hash01(x, y, seed) > 0.93 && x > 2 && y > 2 && x < this.mapSize - 3 && y < this.mapSize - 3) terrain = "rock";
        return { terrain, ore: 0, explored: false, visible: false };
      })
    );

    const player = this.playerBaseCell();
    const enemy = this.enemyBaseCell();
    this.carveRoad(player, enemy);
    this.clearArea(player.x, player.y, 6);
    this.clearArea(enemy.x, enemy.y, 6);
    this.placeOre(player.x + 5, player.y - 5, 5);
    this.placeOre(enemy.x - 5, enemy.y + 5, 5);
    if (this.mode === "large") {
      this.placeOre(Math.floor(this.mapSize * 0.52), Math.floor(this.mapSize * 0.44), 6);
      this.placeOre(Math.floor(this.mapSize * 0.27), Math.floor(this.mapSize * 0.74), 4);
    }

    for (let y = 1; y < this.mapSize - 1; y++) {
      for (let x = 1; x < this.mapSize - 1; x++) {
        const cell = this.map[y][x];
        if (cell.terrain === "clear" || cell.terrain === "rough") {
          const r = hash01(x, y, seed + 88);
          if (r > 0.955) {
            this.decor.push({
              x,
              y,
              image: r > 0.982 ? "treeBrown_large" : r > 0.969 ? "treeGreen_large" : "treeGreen_small",
              scale: r > 0.97 ? 0.9 : 0.7
            });
          } else if (r < 0.012) {
            this.decor.push({
              x,
              y,
              image: r < 0.004 ? "barrelRust_top" : "crateWood",
              scale: 0.78
            });
          }
        }
      }
    }
  }

  private spawnBases(): void {
    const p = this.playerBaseCell();
    const e = this.enemyBaseCell();

    this.addStructure("hq", "player", p.x - 1, p.y - 1);
    this.addStructure("factory", "player", p.x + 2, p.y - 2);
    this.addStructure("refinery", "player", p.x - 4, p.y + 2);
    this.addStructure("power", "player", p.x - 4, p.y - 2);
    this.addStructure("turret", "player", p.x + 2, p.y + 2);

    this.addStructure("hq", "enemy", e.x - 2, e.y - 2);
    this.addStructure("factory", "enemy", e.x - 5, e.y + 1);
    this.addStructure("refinery", "enemy", e.x + 2, e.y - 4);
    this.addStructure("power", "enemy", e.x + 2, e.y + 1);
    this.addStructure("turret", "enemy", e.x - 2, e.y + 2);

    this.addUnit("medium", "player", p.x + 2.2, p.y + 4.2);
    this.addUnit("scout", "player", p.x + 0.8, p.y + 4.8);
    this.addUnit("scout", "player", p.x + 3.3, p.y + 3.1);
    this.addUnit("harvester", "player", p.x - 1.8, p.y + 4.8);
    const hero = this.addUnit("hero", "player", p.x + 4.1, p.y + 1.7);
    hero.heroCooldown = 4;

    this.addUnit("medium", "enemy", e.x - 2.7, e.y - 3.7);
    this.addUnit("scout", "enemy", e.x - 4.4, e.y - 1.8);
    this.addUnit("scout", "enemy", e.x - 1.6, e.y - 4.5);
    this.addUnit("harvester", "enemy", e.x + 4.2, e.y - 2.4);
    if (this.mode === "large") this.addUnit("artillery", "enemy", e.x - 5.2, e.y + 2.8);
  }

  private addUnit(kind: UnitKind, owner: Owner, x: number, y: number): Unit {
    const def = UNIT_DEFS[kind];
    const unit: Unit = {
      id: this.nextId++,
      kind,
      owner,
      x,
      y,
      hp: def.hp,
      maxHp: def.hp,
      facing: owner === "player" ? -0.5 : 2.65,
      turretFacing: owner === "player" ? -0.5 : 2.65,
      order: def.harvester ? "harvest" : "guard",
      path: [],
      cooldown: 0,
      heroCooldown: 0,
      empTimer: 0,
      ore: 0,
      selected: false,
      veteran: 0
    };
    this.units.push(unit);
    return unit;
  }

  private addStructure(kind: StructureKind, owner: Owner, x: number, y: number): Structure {
    const def = STRUCTURE_DEFS[kind];
    const structure: Structure = {
      id: this.nextId++,
      kind,
      owner,
      x,
      y,
      hp: def.hp,
      maxHp: def.hp,
      cooldown: 0
    };
    this.structures.push(structure);
    this.clearArea(x + Math.floor(def.w / 2), y + Math.floor(def.h / 2), Math.max(def.w, def.h) + 1);
    return structure;
  }

  private playerBaseCell(): Vec2 {
    return this.mode === "small" ? { x: 6, y: 14 } : { x: 9, y: 31 };
  }

  private enemyBaseCell(): Vec2 {
    return this.mode === "small" ? { x: 16, y: 7 } : { x: 35, y: 11 };
  }

  private frame(time: number): void {
    const dt = clamp((time - this.lastFrame) / 1000, 0, 0.05);
    this.lastFrame = time;
    this.update(dt);
    this.draw();
    this.raf = requestAnimationFrame((next) => this.frame(next));
  }

  private update(dt: number): void {
    if (this.status === "playing") this.elapsed += dt;
    this.updateCamera(dt);
    if (this.status === "playing") {
      this.updateProduction(dt);
      this.updateEnemy(dt);
      this.updateUnits(dt);
      this.updateStructures(dt);
      this.updateProjectiles(dt);
      this.updateVisibility();
      this.checkEndState();
    }

    for (const explosion of this.explosions) explosion.age += dt;
    this.explosions = this.explosions.filter((e) => e.age < e.duration);

    this.hudTimer += dt;
    if (this.hudTimer > 0.12) {
      this.hudTimer = 0;
      this.updateHud();
    }
  }

  private updateCamera(dt: number): void {
    const speed = 650 / this.camera.zoom;
    let dx = 0;
    let dy = 0;
    if (this.keys.has("arrowleft") || this.keys.has("a")) dx -= 1;
    if (this.keys.has("arrowright") || this.keys.has("d")) dx += 1;
    if (this.keys.has("arrowup") || this.keys.has("w")) dy -= 1;
    if (this.keys.has("arrowdown") || this.keys.has("s")) dy += 1;

    if (this.mouse.inside && this.pointerCanvasTarget()) {
      const margin = 18;
      if (this.mouse.x < margin) dx -= 1;
      if (this.mouse.x > this.width - margin) dx += 1;
      if (this.mouse.y < margin) dy -= 1;
      if (this.mouse.y > this.height - margin) dy += 1;
    }

    if (dx || dy) {
      const len = Math.hypot(dx, dy) || 1;
      this.camera.x += (dx / len) * speed * dt;
      this.camera.y += (dy / len) * speed * dt;
      this.clampCamera();
    }
  }

  private pointerCanvasTarget(): boolean {
    const element = document.elementFromPoint(this.mouse.x, this.mouse.y);
    return element === this.canvas;
  }

  private updateProduction(dt: number): void {
    const active = this.buildQueue[0];
    if (!active) return;
    const powerFactor = this.powerBalance().available < 0 ? 0.55 : 1;
    active.progress += dt * powerFactor;
    if (active.progress >= active.duration) {
      const factory = this.findStructure("factory", "player");
      if (factory) {
        const spawn = this.findNearestPassable(factory.x + 4, factory.y + 2);
        const unit = this.addUnit(active.kind, "player", spawn.x + 0.15, spawn.y + 0.15);
        unit.path = this.findPath(unit, { x: spawn.x + 2, y: spawn.y + 2 });
        unit.order = "move";
        this.audio.play("build", 0.65);
        this.toast(`${UNIT_DEFS[active.kind].name} ready`);
      }
      this.buildQueue.shift();
      this.renderProduction();
    }
  }

  private updateEnemy(dt: number): void {
    this.enemyBuildTimer -= dt;
    this.enemyWaveTimer -= dt;
    const enemyFactory = this.findStructure("factory", "enemy");
    if (enemyFactory && this.enemyBuildTimer <= 0) {
      this.enemyBuildTimer = this.mode === "small" ? 13 : 10;
      const kind: UnitKind = this.elapsed > 120 || Math.random() > 0.58 ? "medium" : Math.random() > 0.7 ? "artillery" : "scout";
      const spawn = this.findNearestPassable(enemyFactory.x - 2, enemyFactory.y + 3);
      this.addUnit(kind, "enemy", spawn.x, spawn.y);
    }

    if (this.enemyWaveTimer <= 0) {
      this.enemyWaveTimer = this.mode === "small" ? 28 : 22;
      const playerHq = this.findStructure("hq", "player");
      if (!playerHq) return;
      const attackers = this.units
        .filter((u) => u.owner === "enemy" && u.kind !== "harvester" && distance(u, this.enemyBaseCell()) < 14)
        .slice(0, this.mode === "small" ? 4 : 7);
      for (const unit of attackers) this.issueUnitAttack(unit, playerHq.id);
      if (attackers.length) this.toast("Enemy armor column detected");
    }
  }

  private updateUnits(dt: number): void {
    for (const unit of this.units) {
      if (unit.hp <= 0) continue;
      const def = UNIT_DEFS[unit.kind];
      unit.cooldown = Math.max(0, unit.cooldown - dt);
      unit.heroCooldown = Math.max(0, unit.heroCooldown - dt);
      unit.empTimer = Math.max(0, unit.empTimer - dt);

      if (def.harvester) {
        this.updateHarvester(unit, dt);
        continue;
      }

      const target = unit.targetId ? this.getTarget(unit.targetId) : undefined;
      if (target && this.isAliveTarget(target) && this.canSeeTarget(unit.owner, target)) {
        this.attackOrApproach(unit, target, dt);
        continue;
      } else if (unit.order === "attack") {
        unit.targetId = undefined;
        unit.order = "guard";
      }

      if (unit.order === "attackMove" || unit.order === "guard" || unit.order === "idle") {
        const acquired = this.acquireTarget(unit, unit.order === "attackMove" ? 7.5 : def.range + 1.5);
        if (acquired) {
          unit.targetId = acquired.id;
          this.attackOrApproach(unit, acquired, dt);
          continue;
        }
      }

      this.followPath(unit, dt);
    }

    this.units = this.units.filter((u) => {
      if (u.hp > 0) return true;
      this.explosions.push({ x: u.x, y: u.y, age: 0, duration: 0.55, radius: 0.65, color: u.owner === "player" ? "#87d9df" : "#e26457" });
      this.audio.play("explosion", 0.58);
      this.selected.delete(u.id);
      return false;
    });
  }

  private updateHarvester(unit: Unit, dt: number): void {
    if (unit.targetId) {
      const target = this.getTarget(unit.targetId);
      if (target && this.isAliveTarget(target) && this.canSeeTarget(unit.owner, target)) {
        this.attackOrApproach(unit, target, dt);
        return;
      }
      unit.targetId = undefined;
    }

    if (unit.ore >= 100) {
      const refinery = this.findStructure("refinery", unit.owner);
      if (!refinery) return;
      const dock = { x: refinery.x + STRUCTURE_DEFS.refinery.w, y: refinery.y + STRUCTURE_DEFS.refinery.h };
      if (distance(unit, dock) < 0.75) {
        if (unit.owner === "player") this.credits += unit.ore * 3;
        unit.ore = 0;
        unit.order = "harvest";
        this.audio.play("build", 0.35);
      } else if (!unit.path.length) {
        unit.path = this.findPath(unit, dock);
        unit.order = "harvest";
      }
      this.followPath(unit, dt);
      return;
    }

    const cell = this.cellAt(Math.round(unit.x), Math.round(unit.y));
    if (cell?.ore && distance(unit, { x: Math.round(unit.x), y: Math.round(unit.y) }) < 0.55) {
      const amount = Math.min(cell.ore, 28 * dt, 100 - unit.ore);
      cell.ore -= amount;
      unit.ore += amount;
      cell.terrain = cell.ore > 2 ? "ore" : "clear";
      return;
    }

    if (!unit.path.length) {
      const ore = this.findNearestOre(unit.x, unit.y, unit.owner);
      if (ore) unit.path = this.findPath(unit, ore);
    }
    this.followPath(unit, dt);
  }

  private updateStructures(dt: number): void {
    for (const structure of this.structures) {
      structure.cooldown = Math.max(0, structure.cooldown - dt);
      if (structure.kind !== "turret") continue;
      const def = STRUCTURE_DEFS[structure.kind];
      const center = this.structureCenter(structure);
      const target = this.acquireTarget({ ...center, owner: structure.owner } as Unit, 5.8);
      if (target && structure.cooldown <= 0) {
        structure.cooldown = 1.25;
        this.spawnProjectile(structure.owner, center, this.targetPoint(target), target.id, 18, 0.28, "#f5db8c", 10.5);
        this.audio.play("attack", 0.32);
      }
    }

    this.structures = this.structures.filter((s) => {
      if (s.hp > 0) return true;
      const c = this.structureCenter(s);
      this.explosions.push({ x: c.x, y: c.y, age: 0, duration: 0.9, radius: Math.max(1.1, STRUCTURE_DEFS[s.kind].w * 0.5), color: "#f09c5a" });
      this.audio.play("explosion", 0.8);
      return false;
    });
  }

  private updateProjectiles(dt: number): void {
    for (const projectile of this.projectiles) {
      const target = projectile.targetId ? this.getTarget(projectile.targetId) : undefined;
      if (target && this.isAliveTarget(target)) {
        const p = this.targetPoint(target);
        projectile.tx = p.x;
        projectile.ty = p.y;
      }

      const d = Math.hypot(projectile.tx - projectile.x, projectile.ty - projectile.y);
      if (d <= projectile.speed * dt) {
        projectile.x = projectile.tx;
        projectile.y = projectile.ty;
        this.impact(projectile);
        projectile.damage = -1;
      } else if (d > 0) {
        projectile.x += ((projectile.tx - projectile.x) / d) * projectile.speed * dt;
        projectile.y += ((projectile.ty - projectile.y) / d) * projectile.speed * dt;
      }
    }
    this.projectiles = this.projectiles.filter((p) => p.damage >= 0);
  }

  private followPath(unit: Unit, dt: number): void {
    if (!unit.path.length || unit.empTimer > 0) return;
    const next = unit.path[0];
    const d = distance(unit, next);
    if (d < 0.08) {
      unit.path.shift();
      if (!unit.path.length && (unit.order === "move" || unit.order === "attackMove")) unit.order = "guard";
      return;
    }

    const def = UNIT_DEFS[unit.kind];
    const terrain = this.cellAt(Math.round(unit.x), Math.round(unit.y))?.terrain ?? "clear";
    const speed = def.speed * TERRAIN_SPEED[terrain] * (unit.empTimer > 0 ? 0.35 : 1);
    const step = Math.min(d, speed * dt);
    unit.x += ((next.x - unit.x) / d) * step;
    unit.y += ((next.y - unit.y) / d) * step;
    const screenA = this.worldToScreen(unit.x, unit.y);
    const screenB = this.worldToScreen(next.x, next.y);
    unit.facing = Math.atan2(screenB.y - screenA.y, screenB.x - screenA.x);
    if (!unit.targetId) unit.turretFacing = unit.facing;
  }

  private attackOrApproach(unit: Unit, target: Unit | Structure, dt: number): void {
    const def = UNIT_DEFS[unit.kind];
    if (def.damage <= 0) return;
    const targetPoint = this.targetPoint(target);
    const d = distance(unit, targetPoint);
    const own = this.worldToScreen(unit.x, unit.y);
    const their = this.worldToScreen(targetPoint.x, targetPoint.y);
    unit.turretFacing = Math.atan2(their.y - own.y, their.x - own.x);

    if (d <= def.range) {
      unit.path = [];
      if (unit.cooldown <= 0) {
        unit.cooldown = def.reload;
        const damage = def.damage * (1 + unit.veteran * 0.12);
        this.spawnProjectile(unit.owner, { x: unit.x, y: unit.y }, targetPoint, target.id, damage, unit.kind === "artillery" ? 0.65 : 0.22, unit.owner === "player" ? "#91e7e9" : "#ff776b", unit.kind === "artillery" ? 8.4 : 11.2);
        this.audio.play("attack", unit.kind === "artillery" ? 0.66 : 0.42);
      }
    } else if (!unit.path.length || unit.order === "attack") {
      unit.path = this.findPath(unit, targetPoint);
    }

    if (unit.path.length) this.followPath(unit, dt);
  }

  private impact(projectile: Projectile): void {
    this.explosions.push({ x: projectile.tx, y: projectile.ty, age: 0, duration: projectile.radius > 0.4 ? 0.6 : 0.32, radius: projectile.radius + 0.35, color: projectile.color });
    const targets = [...this.units, ...this.structures].filter((target) => target.owner !== projectile.owner && this.isAliveTarget(target));
    for (const target of targets) {
      const d = distance(this.targetPoint(target), { x: projectile.tx, y: projectile.ty });
      if (d <= Math.max(0.45, projectile.radius)) {
        const falloff = projectile.radius > 0.3 ? 1 - d / (projectile.radius + 0.25) : 1;
        target.hp -= projectile.damage * clamp(falloff, 0.28, 1);
      }
    }
  }

  private spawnProjectile(owner: Owner, from: Vec2, to: Vec2, targetId: number | undefined, damage: number, radius: number, color: string, speed: number): void {
    this.projectiles.push({
      id: this.nextId++,
      owner,
      x: from.x,
      y: from.y,
      tx: to.x,
      ty: to.y,
      targetId,
      damage,
      radius,
      speed,
      color
    });
  }

  private acquireTarget(actor: Unit, radius: number): Unit | Structure | undefined {
    const candidates = [...this.units, ...this.structures]
      .filter((target) => target.owner !== actor.owner && this.isAliveTarget(target) && this.canSeeTarget(actor.owner, target))
      .map((target) => ({ target, d: distance(actor, this.targetPoint(target)) }))
      .filter((candidate) => candidate.d <= radius)
      .sort((a, b) => a.d - b.d);
    return candidates[0]?.target;
  }

  private canSeeTarget(owner: Owner, target: Unit | Structure): boolean {
    if (owner === "enemy") return true;
    const point = this.targetPoint(target);
    return this.cellAt(Math.round(point.x), Math.round(point.y))?.visible ?? false;
  }

  private getTarget(id: number): Unit | Structure | undefined {
    return this.units.find((u) => u.id === id) ?? this.structures.find((s) => s.id === id);
  }

  private isAliveTarget(target: Unit | Structure): boolean {
    return target.hp > 0;
  }

  private targetPoint(target: Unit | Structure): Vec2 {
    if ("kind" in target && "path" in target) return { x: target.x, y: target.y };
    return this.structureCenter(target as Structure);
  }

  private structureCenter(structure: Structure): Vec2 {
    const def = STRUCTURE_DEFS[structure.kind];
    return { x: structure.x + def.w / 2 - 0.5, y: structure.y + def.h / 2 - 0.5 };
  }

  private checkEndState(): void {
    const enemyHq = this.findStructure("hq", "enemy");
    const playerHq = this.findStructure("hq", "player");
    if (!enemyHq && this.status === "playing") {
      this.status = "victory";
      this.objectiveEl.textContent = "Enemy relay destroyed";
      this.toast("Victory");
      this.audio.play("hero", 0.85);
    }
    if (!playerHq && this.status === "playing") {
      this.status = "defeat";
      this.objectiveEl.textContent = "Command hub lost";
      this.toast("Defeat");
      this.audio.play("alert", 0.85);
    }
  }

  private onPointerDown(event: PointerEvent): void {
    event.preventDefault();
    void this.audio.unlock();
    this.canvas.setPointerCapture(event.pointerId);
    this.mouse.x = event.clientX;
    this.mouse.y = event.clientY;

    const pick = this.pick(event.clientX, event.clientY);
    const startedOnUnit = !!pick.unit && pick.unit.owner === "player";
    const pointer: PointerState = {
      x: event.clientX,
      y: event.clientY,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      button: event.button,
      pointerType: event.pointerType,
      startedOnUnit,
      panning: event.button === 1,
      selecting: false
    };
    this.pointers.set(event.pointerId, pointer);

    if (this.pointers.size === 2) {
      const values = [...this.pointers.values()];
      const center = { x: (values[0].x + values[1].x) / 2, y: (values[0].y + values[1].y) / 2 };
      this.pinchStart = {
        distance: Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y),
        zoom: this.camera.zoom,
        center,
        camera: { x: this.camera.x, y: this.camera.y }
      };
    }
  }

  private onPointerMove(event: PointerEvent): void {
    this.mouse.x = event.clientX;
    this.mouse.y = event.clientY;
    const pointer = this.pointers.get(event.pointerId);
    if (!pointer) return;
    event.preventDefault();

    pointer.lastX = pointer.x;
    pointer.lastY = pointer.y;
    pointer.x = event.clientX;
    pointer.y = event.clientY;

    if (this.pointers.size >= 2 && this.pinchStart) {
      const values = [...this.pointers.values()].slice(0, 2);
      const distanceNow = Math.hypot(values[0].x - values[1].x, values[0].y - values[1].y);
      const centerNow = { x: (values[0].x + values[1].x) / 2, y: (values[0].y + values[1].y) / 2 };
      this.camera.zoom = clamp(this.pinchStart.zoom * (distanceNow / Math.max(1, this.pinchStart.distance)), 0.64, 1.78);
      this.camera.x = this.pinchStart.camera.x - (centerNow.x - this.pinchStart.center.x) / this.camera.zoom;
      this.camera.y = this.pinchStart.camera.y - (centerNow.y - this.pinchStart.center.y) / this.camera.zoom;
      this.clampCamera();
      return;
    }

    const moved = Math.hypot(pointer.x - pointer.startX, pointer.y - pointer.startY);
    const isTouchPan = pointer.pointerType !== "mouse" && moved > 10 && !pointer.startedOnUnit && this.commandMode !== "hero";
    const isMousePan = pointer.button === 1 || (pointer.button === 0 && event.altKey);
    if (isTouchPan || isMousePan || pointer.panning) {
      pointer.panning = true;
      this.camera.x -= (pointer.x - pointer.lastX) / this.camera.zoom;
      this.camera.y -= (pointer.y - pointer.lastY) / this.camera.zoom;
      this.clampCamera();
      this.selectionBox = null;
      return;
    }

    if (pointer.button === 0 && pointer.pointerType === "mouse" && moved > 4) {
      pointer.selecting = true;
      this.selectionBox = {
        start: { x: pointer.startX, y: pointer.startY },
        end: { x: pointer.x, y: pointer.y }
      };
    }
  }

  private onPointerUp(event: PointerEvent): void {
    event.preventDefault();
    const pointer = this.pointers.get(event.pointerId);
    this.pointers.delete(event.pointerId);
    if (!pointer) return;
    this.pinchStart = null;

    if (pointer.selecting && this.selectionBox) {
      this.selectBox(this.selectionBox.start, this.selectionBox.end, event.shiftKey);
      this.selectionBox = null;
      return;
    }

    if (pointer.panning) return;

    const moved = Math.hypot(pointer.x - pointer.startX, pointer.y - pointer.startY);
    if (moved < 8) this.handleWorldClick(pointer.x, pointer.y, pointer.button, pointer.pointerType, event.shiftKey);
  }

  private onPointerCancel(event: PointerEvent): void {
    this.pointers.delete(event.pointerId);
    this.selectionBox = null;
    this.pinchStart = null;
  }

  private handleWorldClick(x: number, y: number, button: number, pointerType: string, additive: boolean): void {
    const pick = this.pick(x, y);
    const selectedUnits = this.selectedUnits();
    const useTouchCommand = pointerType !== "mouse" && selectedUnits.length > 0;

    if (this.commandMode === "hero" && selectedUnits.some((u) => UNIT_DEFS[u.kind].hero)) {
      const hero = selectedUnits.find((u) => UNIT_DEFS[u.kind].hero);
      if (hero && hero.heroCooldown <= 0) {
        this.castHero(hero, pick.cell);
        this.commandMode = null;
        this.updateCommandButtons();
      }
      return;
    }

    if (button === 2 || useTouchCommand || this.commandMode === "attackMove" || this.commandMode === "guard") {
      if (!selectedUnits.length) return;
      if (pick.unit?.owner === "enemy" || pick.structure?.owner === "enemy") {
        const target = (pick.unit ?? pick.structure)!;
        for (const unit of selectedUnits) this.issueUnitAttack(unit, target.id);
        this.audio.play("attack", 0.62);
        this.toast("Attack order confirmed");
      } else if (this.commandMode === "guard") {
        for (const unit of selectedUnits) {
          unit.guard = { x: pick.cell.x, y: pick.cell.y };
          unit.path = this.findPath(unit, pick.cell);
          unit.order = "guard";
          unit.targetId = undefined;
        }
        this.audio.play("move", 0.5);
      } else {
        this.issueMoveOrder(selectedUnits, pick.cell, this.commandMode === "attackMove");
        this.audio.play("move", 0.55);
      }
      if (this.commandMode !== "attackMove") {
        this.commandMode = null;
        this.updateCommandButtons();
      }
      return;
    }

    if (button === 0) {
      if (pick.unit?.owner === "player") {
        if (!additive) this.clearSelection();
        pick.unit.selected = true;
        this.selected.add(pick.unit.id);
        this.audio.play("select", 0.5);
      } else if (pick.structure?.owner === "player") {
        if (!additive) this.clearSelection();
        this.audio.play("select", 0.35);
        this.toast(STRUCTURE_DEFS[pick.structure.kind].name);
      } else if (!additive) {
        this.clearSelection();
      }
      this.updateHud();
    }
  }

  private castHero(hero: Unit, cell: Vec2): void {
    hero.heroCooldown = 28;
    this.audio.play("hero", 0.9);
    this.toast("Hero strike ready");
    for (let i = 0; i < 7; i++) {
      const angle = (Math.PI * 2 * i) / 7;
      const r = i === 0 ? 0 : 0.8 + (i % 3) * 0.35;
      const x = cell.x + Math.cos(angle) * r;
      const y = cell.y + Math.sin(angle) * r;
      setTimeout(() => {
        this.explosions.push({ x, y, age: 0, duration: 0.8, radius: 1.3, color: "#87d9df" });
        for (const target of [...this.units, ...this.structures]) {
          if (target.owner === hero.owner || !this.isAliveTarget(target)) continue;
          const d = distance(this.targetPoint(target), { x, y });
          if (d < 1.6) {
            target.hp -= 54 * (1 - d / 2.1);
            if ("empTimer" in target) target.empTimer = Math.max(target.empTimer, 3.8);
          }
        }
        this.audio.play("explosion", 0.55);
      }, i * 110);
    }
  }

  private issueUnitAttack(unit: Unit, targetId: number): void {
    if (UNIT_DEFS[unit.kind].damage <= 0) return;
    unit.targetId = targetId;
    unit.order = "attack";
    unit.path = [];
  }

  private issueMoveOrder(units: Unit[], cell: Vec2, attackMove: boolean): void {
    const formation = this.formationOffsets(units.length);
    units.forEach((unit, index) => {
      const dest = this.findNearestPassable(cell.x + formation[index].x, cell.y + formation[index].y);
      unit.path = this.findPath(unit, dest);
      unit.targetId = undefined;
      unit.order = attackMove ? "attackMove" : "move";
      unit.guard = dest;
    });
    this.toast(attackMove ? "Attack move" : "Move order");
  }

  private formationOffsets(count: number): Vec2[] {
    if (count <= 1) return [{ x: 0, y: 0 }];
    const offsets: Vec2[] = [];
    const cols = Math.ceil(Math.sqrt(count));
    for (let i = 0; i < count; i++) {
      offsets.push({ x: (i % cols) - (cols - 1) / 2, y: Math.floor(i / cols) - Math.floor(count / cols) / 2 });
    }
    return offsets;
  }

  private stopSelected(): void {
    for (const unit of this.selectedUnits()) {
      unit.path = [];
      unit.targetId = undefined;
      unit.order = "idle";
    }
    this.commandMode = null;
    this.updateCommandButtons();
    this.audio.play("select", 0.32);
  }

  private scatterSelected(): void {
    const selected = this.selectedUnits();
    selected.forEach((unit, index) => {
      const angle = (index / Math.max(1, selected.length)) * Math.PI * 2 + hash01(unit.id, this.elapsed, 4) * 1.3;
      const dest = this.findNearestPassable(unit.x + Math.cos(angle) * 3.2, unit.y + Math.sin(angle) * 3.2);
      unit.path = this.findPath(unit, dest);
      unit.targetId = undefined;
      unit.order = "move";
    });
    this.audio.play("move", 0.45);
  }

  private toggleMode(mode: Exclude<CommandMode, null>): void {
    if (mode === "hero") {
      const hero = this.selectedUnits().find((unit) => UNIT_DEFS[unit.kind].hero);
      if (!hero || hero.heroCooldown > 0) {
        this.audio.play("alert", 0.35);
        return;
      }
    }
    this.commandMode = this.commandMode === mode ? null : mode;
    this.updateCommandButtons();
  }

  private clearSelection(): void {
    for (const unit of this.units) unit.selected = false;
    this.selected.clear();
  }

  private selectBox(a: Vec2, b: Vec2, additive: boolean): void {
    if (!additive) this.clearSelection();
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);
    for (const unit of this.units) {
      if (unit.owner !== "player") continue;
      const p = this.worldToClient(unit.x, unit.y);
      if (p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY) {
        unit.selected = true;
        this.selected.add(unit.id);
      }
    }
    if (this.selected.size) this.audio.play("select", 0.54);
    this.updateHud();
  }

  private selectedUnits(): Unit[] {
    return this.units.filter((unit) => unit.owner === "player" && this.selected.has(unit.id));
  }

  private onMinimapPointer(event: PointerEvent): void {
    event.preventDefault();
    const rect = this.minimap.getBoundingClientRect();
    const x = clamp((event.clientX - rect.left) / rect.width, 0, 1) * (this.mapSize - 1);
    const y = clamp((event.clientY - rect.top) / rect.height, 0, 1) * (this.mapSize - 1);
    const p = this.worldToScreen(x, y);
    this.camera.x = p.x;
    this.camera.y = p.y;
    this.clampCamera();
  }

  private pick(clientX: number, clientY: number): { unit?: Unit; structure?: Structure; cell: Vec2 } {
    const cell = this.clientToCell(clientX, clientY);
    const visibleEnemies = this.units
      .filter((unit) => unit.owner === "player" || (this.cellAt(Math.round(unit.x), Math.round(unit.y))?.visible ?? false))
      .sort((a, b) => this.worldToScreen(b.x, b.y).y - this.worldToScreen(a.x, a.y).y);
    for (const unit of visibleEnemies) {
      const p = this.worldToClient(unit.x, unit.y);
      const radius = UNIT_DEFS[unit.kind].hero ? 20 : 24;
      if (Math.hypot(clientX - p.x, clientY - p.y) < radius) return { unit, cell };
    }

    for (const structure of [...this.structures].sort((a, b) => this.structureCenter(b).y - this.structureCenter(a).y)) {
      if (structure.owner === "enemy" && !(this.cellAt(Math.round(this.structureCenter(structure).x), Math.round(this.structureCenter(structure).y))?.visible ?? false)) continue;
      const def = STRUCTURE_DEFS[structure.kind];
      if (cell.x >= structure.x - 0.5 && cell.x <= structure.x + def.w - 0.5 && cell.y >= structure.y - 0.5 && cell.y <= structure.y + def.h - 0.5) {
        return { structure, cell };
      }
    }

    return { cell };
  }

  private draw(): void {
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.fillStyle = "#07100d";
    this.ctx.fillRect(0, 0, this.width, this.height);

    this.ctx.save();
    this.ctx.translate(this.width / 2, this.height / 2);
    this.ctx.scale(this.camera.zoom, this.camera.zoom);
    this.ctx.translate(-this.camera.x, -this.camera.y);

    this.drawMap();
    this.drawDecor();
    this.drawActors();
    this.drawProjectiles();
    this.drawExplosions();
    this.drawFog();
    this.drawHeroReticle();

    this.ctx.restore();
    this.drawSelectionBox();
    this.drawStatusOverlay();
    this.drawMinimap();
  }

  private drawMap(): void {
    for (let y = 0; y < this.mapSize; y++) {
      for (let x = 0; x < this.mapSize; x++) {
        const p = this.worldToScreen(x, y);
        if (!this.projectedVisible(p, TILE_W * 1.2, TILE_H * 1.2)) continue;
        this.drawTile(x, y, p);
      }
    }
  }

  private drawTile(x: number, y: number, p: Vec2): void {
    const cell = this.map[y][x];
    const shade = (hash01(x, y, 42) - 0.5) * 18;
    this.ctx.beginPath();
    this.ctx.moveTo(p.x, p.y - HALF_H);
    this.ctx.lineTo(p.x + HALF_W, p.y);
    this.ctx.lineTo(p.x, p.y + HALF_H);
    this.ctx.lineTo(p.x - HALF_W, p.y);
    this.ctx.closePath();
    this.ctx.fillStyle = this.tint(TERRAIN_COLORS[cell.terrain], shade);
    this.ctx.fill();

    if (cell.terrain === "road") {
      this.ctx.strokeStyle = "rgba(238, 214, 151, 0.18)";
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.moveTo(p.x - HALF_W * 0.82, p.y);
      this.ctx.lineTo(p.x + HALF_W * 0.82, p.y);
      this.ctx.stroke();
    }

    if (cell.terrain === "water") {
      this.ctx.strokeStyle = "rgba(150, 221, 230, 0.18)";
      this.ctx.lineWidth = 1;
      this.ctx.beginPath();
      this.ctx.moveTo(p.x - 18, p.y - 3);
      this.ctx.lineTo(p.x + 18, p.y + 3);
      this.ctx.stroke();
    }

    if (cell.ore > 0) {
      const count = Math.min(5, Math.ceil(cell.ore / 22));
      for (let i = 0; i < count; i++) {
        const ox = (hash01(x, y, i) - 0.5) * HALF_W * 1.1;
        const oy = (hash01(y, x, i + 12) - 0.5) * HALF_H * 1.1;
        this.ctx.fillStyle = i % 2 ? "#a7d454" : "#61e870";
        this.ctx.beginPath();
        this.ctx.moveTo(p.x + ox, p.y + oy - 4);
        this.ctx.lineTo(p.x + ox + 5, p.y + oy + 2);
        this.ctx.lineTo(p.x + ox - 2, p.y + oy + 5);
        this.ctx.closePath();
        this.ctx.fill();
      }
    }

    this.ctx.strokeStyle = "rgba(5, 12, 10, 0.22)";
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  private drawDecor(): void {
    const visibleDecor = this.decor
      .filter((item) => this.cellAt(item.x, item.y)?.explored)
      .map((item) => ({ item, p: this.worldToScreen(item.x, item.y) }))
      .filter(({ p }) => this.projectedVisible(p, 80, 80))
      .sort((a, b) => a.p.y - b.p.y);

    for (const { item, p } of visibleDecor) {
      const image = this.assets.get(item.image);
      if (!image) continue;
      const scale = item.scale;
      const w = image.width * scale;
      const h = image.height * scale;
      this.ctx.globalAlpha = this.cellAt(item.x, item.y)?.visible ? 1 : 0.45;
      this.ctx.drawImage(image, p.x - w / 2, p.y - h + 12, w, h);
      this.ctx.globalAlpha = 1;
    }
  }

  private drawActors(): void {
    const drawables: { y: number; draw: () => void }[] = [];
    for (const structure of this.structures) {
      const center = this.structureCenter(structure);
      if (structure.owner === "enemy" && !(this.cellAt(Math.round(center.x), Math.round(center.y))?.visible ?? false)) continue;
      if (this.projectedVisible(this.worldToScreen(center.x, center.y), 240, 180)) {
        drawables.push({ y: this.worldToScreen(center.x, center.y).y + STRUCTURE_DEFS[structure.kind].h * HALF_H, draw: () => this.drawStructure(structure) });
      }
    }
    for (const unit of this.units) {
      const visible = unit.owner === "player" || (this.cellAt(Math.round(unit.x), Math.round(unit.y))?.visible ?? false);
      if (!visible) continue;
      const p = this.worldToScreen(unit.x, unit.y);
      if (this.projectedVisible(p, 110, 110)) drawables.push({ y: p.y + 14, draw: () => this.drawUnit(unit) });
    }
    drawables.sort((a, b) => a.y - b.y);
    for (const drawable of drawables) drawable.draw();
  }

  private drawStructure(structure: Structure): void {
    const def = STRUCTURE_DEFS[structure.kind];
    const p1 = this.worldToScreen(structure.x - 0.5, structure.y - 0.5);
    const p2 = this.worldToScreen(structure.x + def.w - 0.5, structure.y - 0.5);
    const p3 = this.worldToScreen(structure.x + def.w - 0.5, structure.y + def.h - 0.5);
    const p4 = this.worldToScreen(structure.x - 0.5, structure.y + def.h - 0.5);
    const top = [p1, p2, p3, p4].map((p) => ({ x: p.x, y: p.y - def.height }));

    const ownerAccent = structure.owner === "player" ? def.accent : "#e26457";
    this.ctx.globalAlpha = this.cellAt(Math.round(this.structureCenter(structure).x), Math.round(this.structureCenter(structure).y))?.visible ? 1 : 0.42;

    this.pathPolygon([top[1], p2, p3, top[2]], this.tint(def.side, -20), "rgba(0,0,0,0.28)");
    this.pathPolygon([top[2], p3, p4, top[3]], this.tint(def.side, -35), "rgba(0,0,0,0.32)");
    this.pathPolygon(top, def.top, "rgba(235,245,225,0.18)");

    this.ctx.strokeStyle = ownerAccent;
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.moveTo(top[0].x, top[0].y);
    this.ctx.lineTo(top[1].x, top[1].y);
    this.ctx.lineTo(top[2].x, top[2].y);
    this.ctx.stroke();

    if (structure.kind === "hq") this.drawRadarDish(this.structureCenter(structure), ownerAccent);
    if (structure.kind === "factory") this.drawFactoryDoor(top, ownerAccent);
    if (structure.kind === "refinery") this.drawRefineryPipes(top, ownerAccent);
    if (structure.kind === "power") this.drawPowerStacks(top, ownerAccent);
    if (structure.kind === "turret") this.drawTurret(structure, ownerAccent);

    this.drawHealthBar(this.worldToScreen(this.structureCenter(structure).x, this.structureCenter(structure).y).x, top[0].y - 14, structure.hp / structure.maxHp, structure.owner);
    this.ctx.globalAlpha = 1;
  }

  private drawUnit(unit: Unit): void {
    const p = this.worldToScreen(unit.x, unit.y);
    const def = this.unitVisualDef(unit);
    const visible = this.cellAt(Math.round(unit.x), Math.round(unit.y))?.visible ?? true;
    this.ctx.globalAlpha = visible || unit.owner === "player" ? 1 : 0.4;

    this.ctx.fillStyle = "rgba(0,0,0,0.35)";
    this.ctx.beginPath();
    this.ctx.ellipse(p.x, p.y + 8, UNIT_DEFS[unit.kind].hero ? 11 : 22, UNIT_DEFS[unit.kind].hero ? 6 : 10, 0, 0, Math.PI * 2);
    this.ctx.fill();

    if (unit.selected) {
      this.ctx.strokeStyle = UNIT_DEFS[unit.kind].hero ? "#f5db8c" : "#87d9df";
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.ellipse(p.x, p.y + 6, UNIT_DEFS[unit.kind].hero ? 18 : 29, UNIT_DEFS[unit.kind].hero ? 10 : 16, 0, 0, Math.PI * 2);
      this.ctx.stroke();
    }

    if (UNIT_DEFS[unit.kind].hero) {
      this.drawHeroUnit(p, unit);
    } else {
      const body = def.body ? this.assets.get(def.body) : undefined;
      const turret = def.turret ? this.assets.get(def.turret) : undefined;
      if (body) this.drawRotated(body, p.x, p.y - 5, body.width * 1.12, body.height * 1.12, unit.facing + Math.PI / 2);
      if (turret) this.drawRotated(turret, p.x, p.y - 13, turret.width * 1.06, turret.height * 1.06, unit.turretFacing + Math.PI / 2);
    }

    this.drawHealthBar(p.x, p.y - (UNIT_DEFS[unit.kind].hero ? 34 : 40), unit.hp / unit.maxHp, unit.owner);
    if (unit.empTimer > 0) {
      this.ctx.strokeStyle = "rgba(135, 217, 223, 0.75)";
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y - 6, 22 + Math.sin(this.elapsed * 10) * 3, 0, Math.PI * 2);
      this.ctx.stroke();
    }
    this.ctx.globalAlpha = 1;
  }

  private drawHeroUnit(p: Vec2, unit: Unit): void {
    const accent = unit.owner === "player" ? "#f5db8c" : "#e26457";
    this.ctx.fillStyle = "#1b2a26";
    this.ctx.fillRect(p.x - 5, p.y - 27, 10, 20);
    this.ctx.fillStyle = accent;
    this.ctx.fillRect(p.x - 7, p.y - 18, 14, 4);
    this.ctx.fillStyle = "#d8bd93";
    this.ctx.beginPath();
    this.ctx.arc(p.x, p.y - 31, 5, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.strokeStyle = "#121717";
    this.ctx.lineWidth = 3;
    this.ctx.beginPath();
    this.ctx.moveTo(p.x + 6, p.y - 22);
    this.ctx.lineTo(p.x + Math.cos(unit.turretFacing) * 18, p.y - 22 + Math.sin(unit.turretFacing) * 10);
    this.ctx.stroke();
  }

  private drawProjectiles(): void {
    for (const projectile of this.projectiles) {
      const p = this.worldToScreen(projectile.x, projectile.y);
      const t = this.worldToScreen(projectile.tx, projectile.ty);
      const angle = Math.atan2(t.y - p.y, t.x - p.x);
      const image = this.assets.get(projectile.radius > 0.4 ? "shotLarge" : "shotThin");
      if (image) {
        this.drawRotated(image, p.x, p.y - 12, image.width * 0.75, image.height * 0.75, angle + Math.PI / 2);
      } else {
        this.ctx.fillStyle = projectile.color;
        this.ctx.beginPath();
        this.ctx.arc(p.x, p.y - 10, 3, 0, Math.PI * 2);
        this.ctx.fill();
      }
    }
  }

  private drawExplosions(): void {
    for (const explosion of this.explosions) {
      const t = explosion.age / explosion.duration;
      const p = this.worldToScreen(explosion.x, explosion.y);
      const radius = explosion.radius * TILE_H * (0.4 + t * 0.85);
      this.ctx.globalAlpha = 1 - t;
      this.ctx.fillStyle = explosion.color;
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y - 16, radius, 0, Math.PI * 2);
      this.ctx.fill();
      const frame = Math.min(5, Math.max(1, Math.ceil(t * 5)));
      const image = this.assets.get(`explosion${frame}`);
      if (image) {
        const size = radius * 2.4;
        this.ctx.drawImage(image, p.x - size / 2, p.y - 28 - size / 2, size, size);
      }
      this.ctx.globalAlpha = 1;
    }
  }

  private drawFog(): void {
    for (let y = 0; y < this.mapSize; y++) {
      for (let x = 0; x < this.mapSize; x++) {
        const cell = this.map[y][x];
        if (cell.visible) continue;
        const p = this.worldToScreen(x, y);
        if (!this.projectedVisible(p, TILE_W, TILE_H)) continue;
        this.ctx.beginPath();
        this.ctx.moveTo(p.x, p.y - HALF_H);
        this.ctx.lineTo(p.x + HALF_W, p.y);
        this.ctx.lineTo(p.x, p.y + HALF_H);
        this.ctx.lineTo(p.x - HALF_W, p.y);
        this.ctx.closePath();
        this.ctx.fillStyle = cell.explored ? "rgba(3, 7, 8, 0.42)" : "rgba(2, 4, 5, 0.88)";
        this.ctx.fill();
      }
    }
  }

  private drawHeroReticle(): void {
    if (this.commandMode !== "hero") return;
    const cell = this.clientToCell(this.mouse.x, this.mouse.y);
    const p = this.worldToScreen(cell.x, cell.y);
    this.ctx.strokeStyle = "#f5db8c";
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.arc(p.x, p.y, TILE_H * 1.55, 0, Math.PI * 2);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(p.x - 16, p.y);
    this.ctx.lineTo(p.x + 16, p.y);
    this.ctx.moveTo(p.x, p.y - 16);
    this.ctx.lineTo(p.x, p.y + 16);
    this.ctx.stroke();
  }

  private drawSelectionBox(): void {
    if (!this.selectionBox) return;
    const x = Math.min(this.selectionBox.start.x, this.selectionBox.end.x);
    const y = Math.min(this.selectionBox.start.y, this.selectionBox.end.y);
    const w = Math.abs(this.selectionBox.start.x - this.selectionBox.end.x);
    const h = Math.abs(this.selectionBox.start.y - this.selectionBox.end.y);
    this.ctx.save();
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.fillStyle = "rgba(135, 217, 223, 0.12)";
    this.ctx.strokeStyle = "rgba(135, 217, 223, 0.85)";
    this.ctx.lineWidth = 1.5;
    this.ctx.fillRect(x, y, w, h);
    this.ctx.strokeRect(x, y, w, h);
    this.ctx.restore();
  }

  private drawStatusOverlay(): void {
    if (this.status === "playing") return;
    this.ctx.save();
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.fillStyle = "rgba(6, 10, 10, 0.58)";
    this.ctx.fillRect(0, 0, this.width, this.height);
    this.ctx.fillStyle = this.status === "victory" ? "#f5db8c" : "#e26457";
    this.ctx.font = "900 42px Inter, system-ui, sans-serif";
    this.ctx.textAlign = "center";
    this.ctx.fillText(this.status === "victory" ? "Victory" : "Defeat", this.width / 2, this.height / 2);
    this.ctx.restore();
  }

  private drawMinimap(): void {
    const rect = this.minimap.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    this.miniCtx.clearRect(0, 0, w, h);
    this.miniCtx.fillStyle = "#0a1311";
    this.miniCtx.fillRect(0, 0, w, h);
    const sx = w / this.mapSize;
    const sy = h / this.mapSize;

    for (let y = 0; y < this.mapSize; y++) {
      for (let x = 0; x < this.mapSize; x++) {
        const cell = this.map[y][x];
        this.miniCtx.fillStyle = cell.explored ? TERRAIN_COLORS[cell.terrain] : "#050808";
        if (!cell.visible && cell.explored) this.miniCtx.globalAlpha = 0.48;
        this.miniCtx.fillRect(x * sx, y * sy, Math.ceil(sx), Math.ceil(sy));
        this.miniCtx.globalAlpha = 1;
      }
    }

    for (const structure of this.structures) {
      const center = this.structureCenter(structure);
      if (structure.owner === "enemy" && !(this.cellAt(Math.round(center.x), Math.round(center.y))?.visible ?? false)) continue;
      this.miniCtx.fillStyle = structure.owner === "player" ? "#87d9df" : "#e26457";
      this.miniCtx.fillRect(center.x * sx - 2, center.y * sy - 2, 4, 4);
    }
    for (const unit of this.units) {
      if (unit.owner === "enemy" && !(this.cellAt(Math.round(unit.x), Math.round(unit.y))?.visible ?? false)) continue;
      this.miniCtx.fillStyle = unit.owner === "player" ? "#f5db8c" : "#ff786c";
      this.miniCtx.fillRect(unit.x * sx - 1.5, unit.y * sy - 1.5, 3, 3);
    }

    const tl = this.clientToCell(0, 0);
    const br = this.clientToCell(this.width, this.height);
    const minX = clamp(Math.min(tl.x, br.x), 0, this.mapSize);
    const minY = clamp(Math.min(tl.y, br.y), 0, this.mapSize);
    const maxX = clamp(Math.max(tl.x, br.x), 0, this.mapSize);
    const maxY = clamp(Math.max(tl.y, br.y), 0, this.mapSize);
    this.miniCtx.strokeStyle = "#f5db8c";
    this.miniCtx.lineWidth = 1;
    this.miniCtx.strokeRect(minX * sx, minY * sy, Math.max(8, (maxX - minX) * sx), Math.max(8, (maxY - minY) * sy));
  }

  private updateHud(): void {
    this.creditsEl.textContent = Math.floor(this.credits).toString();
    const power = this.powerBalance();
    this.powerEl.textContent = `${power.available}/${power.generated}`;
    this.powerEl.style.color = power.available < 0 ? "#ff8b78" : "";
    this.timeEl.textContent = formatTime(this.elapsed);

    if (this.status === "playing") {
      const enemyHq = this.findStructure("hq", "enemy");
      this.objectiveEl.textContent = enemyHq ? `Enemy relay integrity ${Math.ceil((enemyHq.hp / enemyHq.maxHp) * 100)}%` : "Enemy relay destroyed";
    }

    const selected = this.selectedUnits();
    this.selectionSummary.textContent = selected.length
      ? `${selected.length} selected`
      : "No units selected";
    this.unitCards.innerHTML = selected
      .slice(0, 9)
      .map((unit) => {
        const def = UNIT_DEFS[unit.kind];
        const hp = clamp((unit.hp / unit.maxHp) * 100, 0, 100);
        return `<div class="unit-card"><strong>${def.name}</strong><span class="health-rail"><span class="health-fill" style="--hp:${hp}%"></span></span></div>`;
      })
      .join("");

    const hero = selected.find((unit) => UNIT_DEFS[unit.kind].hero);
    this.heroButton.disabled = !hero || hero.heroCooldown > 0;
    this.heroButton.classList.toggle("ready", !!hero && hero.heroCooldown <= 0);
    this.heroButtonText.textContent = hero && hero.heroCooldown > 0 ? Math.ceil(hero.heroCooldown).toString() : "Hero";

    const active = this.buildQueue[0];
    this.queueState.textContent = active ? `${Math.floor((active.progress / active.duration) * 100)}%` : "Ready";
    this.updateProductionProgress();
    this.updateCommandButtons();
  }

  private renderProduction(): void {
    const options: UnitKind[] = ["scout", "medium", "artillery", "harvester"];
    this.productionList.innerHTML = options
      .map((kind) => {
        const def = UNIT_DEFS[kind];
        return `<button class="production-button" data-build="${kind}" type="button">
          <span class="production-progress"></span>
          <span class="production-icon"><i data-lucide="${def.icon}"></i></span>
          <span class="production-main"><span class="production-name">${def.name}</span><span class="production-meta">${def.role}</span></span>
          <span class="production-cost">${def.cost}</span>
        </button>`;
      })
      .join("");
    createIcons({ icons });
    this.productionList.querySelectorAll<HTMLButtonElement>("[data-build]").forEach((button) => {
      button.addEventListener("click", () => {
        const kind = button.dataset.build as UnitKind;
        this.queueBuild(kind);
      });
    });
    this.updateProductionProgress();
  }

  private updateProductionProgress(): void {
    const active = this.buildQueue[0];
    this.productionList.querySelectorAll<HTMLButtonElement>("[data-build]").forEach((button) => {
      const kind = button.dataset.build as UnitKind;
      const def = UNIT_DEFS[kind];
      const progress = active?.kind === kind ? clamp((active.progress / active.duration) * 100, 0, 100) : 0;
      button.style.setProperty("--progress", `${progress}%`);
      button.disabled = this.credits < def.cost || !this.findStructure("factory", "player") || this.status !== "playing";
    });
  }

  private queueBuild(kind: UnitKind): void {
    const def = UNIT_DEFS[kind];
    if (this.credits < def.cost || !this.findStructure("factory", "player")) {
      this.audio.play("alert", 0.35);
      return;
    }
    this.credits -= def.cost;
    this.buildQueue.push({ kind, progress: 0, duration: def.time });
    this.audio.play("build", 0.45);
    this.toast(`${def.name} queued`);
    this.updateHud();
  }

  private updateCommandButtons(): void {
    document.querySelectorAll<HTMLButtonElement>(".command-button[data-mode]").forEach((button) => {
      button.classList.toggle("active", button.dataset.mode === this.commandMode);
    });
  }

  private powerBalance(): { generated: number; used: number; available: number } {
    let generated = 0;
    let used = 0;
    for (const structure of this.structures.filter((s) => s.owner === "player")) {
      const value = STRUCTURE_DEFS[structure.kind].power;
      if (value > 0) generated += value;
      else used += Math.abs(value);
    }
    return { generated, used, available: generated - used };
  }

  private updateVisibility(): void {
    for (const row of this.map) {
      for (const cell of row) cell.visible = false;
    }

    const revealers: { x: number; y: number; r: number }[] = [];
    for (const unit of this.units.filter((u) => u.owner === "player")) revealers.push({ x: unit.x, y: unit.y, r: UNIT_DEFS[unit.kind].reveal });
    for (const structure of this.structures.filter((s) => s.owner === "player")) {
      const center = this.structureCenter(structure);
      revealers.push({ x: center.x, y: center.y, r: STRUCTURE_DEFS[structure.kind].reveal });
    }

    for (const revealer of revealers) {
      const minX = Math.floor(revealer.x - revealer.r);
      const maxX = Math.ceil(revealer.x + revealer.r);
      const minY = Math.floor(revealer.y - revealer.r);
      const maxY = Math.ceil(revealer.y + revealer.r);
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const cell = this.cellAt(x, y);
          if (!cell) continue;
          if (Math.hypot(revealer.x - x, revealer.y - y) <= revealer.r) {
            cell.visible = true;
            cell.explored = true;
          }
        }
      }
    }
  }

  private findPath(unit: Unit, dest: Vec2): Vec2[] {
    const start = this.findNearestPassable(Math.round(unit.x), Math.round(unit.y));
    const goal = this.findNearestPassable(Math.round(dest.x), Math.round(dest.y));
    const startKey = `${start.x},${start.y}`;
    const goalKey = `${goal.x},${goal.y}`;
    if (startKey === goalKey) return [];

    const frontier = new MinHeap<Vec2>();
    frontier.push(0, start);
    const came = new Map<string, string>();
    const cost = new Map<string, number>();
    cost.set(startKey, 0);
    const neighbors = [
      { x: 1, y: 0 },
      { x: -1, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: -1 },
      { x: 1, y: 1 },
      { x: -1, y: -1 },
      { x: 1, y: -1 },
      { x: -1, y: 1 }
    ];

    let found = false;
    while (frontier.size) {
      const current = frontier.pop()!;
      const currentKey = `${current.x},${current.y}`;
      if (currentKey === goalKey) {
        found = true;
        break;
      }

      for (const n of neighbors) {
        const next = { x: current.x + n.x, y: current.y + n.y };
        const cell = this.cellAt(next.x, next.y);
        if (!cell || !this.isPassable(next.x, next.y, unit)) continue;
        const stepCost = (n.x && n.y ? 1.42 : 1) / Math.max(0.3, TERRAIN_SPEED[cell.terrain]);
        const newCost = (cost.get(currentKey) ?? Infinity) + stepCost;
        const nextKey = `${next.x},${next.y}`;
        if (newCost < (cost.get(nextKey) ?? Infinity)) {
          cost.set(nextKey, newCost);
          const priority = newCost + Math.hypot(goal.x - next.x, goal.y - next.y);
          frontier.push(priority, next);
          came.set(nextKey, currentKey);
        }
      }
    }

    if (!found) return [];
    const path: Vec2[] = [];
    let current = goalKey;
    while (current !== startKey) {
      const [x, y] = current.split(",").map(Number);
      path.push({ x, y });
      current = came.get(current)!;
    }
    path.reverse();
    return path.map((p) => ({ x: p.x, y: p.y }));
  }

  private isPassable(x: number, y: number, unit?: Unit): boolean {
    const cell = this.cellAt(x, y);
    if (!cell) return false;
    if (cell.terrain === "water" || cell.terrain === "rock") return false;
    for (const structure of this.structures) {
      const def = STRUCTURE_DEFS[structure.kind];
      if (x >= structure.x && x < structure.x + def.w && y >= structure.y && y < structure.y + def.h) return false;
    }
    if (unit?.kind === "harvester" && cell.terrain === "ore") return true;
    return true;
  }

  private findNearestPassable(x: number, y: number): Vec2 {
    const origin = { x: Math.round(x), y: Math.round(y) };
    if (this.isPassable(origin.x, origin.y)) return origin;
    for (let r = 1; r < 8; r++) {
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
          const p = { x: origin.x + dx, y: origin.y + dy };
          if (this.isPassable(p.x, p.y)) return p;
        }
      }
    }
    return { x: clamp(origin.x, 0, this.mapSize - 1), y: clamp(origin.y, 0, this.mapSize - 1) };
  }

  private findNearestOre(x: number, y: number, owner: Owner): Vec2 | undefined {
    let best: { x: number; y: number; d: number } | undefined;
    for (let cy = 0; cy < this.mapSize; cy++) {
      for (let cx = 0; cx < this.mapSize; cx++) {
        const cell = this.map[cy][cx];
        if (cell.ore <= 4) continue;
        if (owner === "player" && !cell.explored) continue;
        const d = Math.hypot(cx - x, cy - y);
        if (!best || d < best.d) best = { x: cx, y: cy, d };
      }
    }
    return best;
  }

  private cellAt(x: number, y: number): Cell | undefined {
    if (x < 0 || y < 0 || x >= this.mapSize || y >= this.mapSize) return undefined;
    return this.map[y][x];
  }

  private clearArea(cx: number, cy: number, radius: number): void {
    for (let y = Math.floor(cy - radius); y <= Math.ceil(cy + radius); y++) {
      for (let x = Math.floor(cx - radius); x <= Math.ceil(cx + radius); x++) {
        const cell = this.cellAt(x, y);
        if (cell && Math.hypot(cx - x, cy - y) <= radius) {
          cell.terrain = "clear";
          cell.ore = 0;
        }
      }
    }
  }

  private carveRoad(a: Vec2, b: Vec2): void {
    const steps = Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)) * 2;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = Math.round(lerp(a.x, b.x, t));
      const y = Math.round(lerp(a.y, b.y, t));
      for (let oy = -1; oy <= 1; oy++) {
        for (let ox = -1; ox <= 1; ox++) {
          const cell = this.cellAt(x + ox, y + oy);
          if (cell && cell.terrain !== "water") {
            cell.terrain = Math.abs(ox) + Math.abs(oy) <= 1 ? "road" : "clear";
          }
        }
      }
    }
  }

  private placeOre(cx: number, cy: number, radius: number): void {
    for (let y = Math.floor(cy - radius); y <= Math.ceil(cy + radius); y++) {
      for (let x = Math.floor(cx - radius); x <= Math.ceil(cx + radius); x++) {
        const cell = this.cellAt(x, y);
        if (!cell) continue;
        const d = Math.hypot(cx - x, cy - y);
        if (d <= radius && cell.terrain !== "water" && cell.terrain !== "rock") {
          cell.terrain = "ore";
          cell.ore = Math.max(cell.ore, (1 - d / radius) * 100 + hash01(x, y, radius) * 40);
        }
      }
    }
  }

  private findStructure(kind: StructureKind, owner: Owner): Structure | undefined {
    return this.structures.find((structure) => structure.kind === kind && structure.owner === owner && structure.hp > 0);
  }

  private centerOnPlayerBase(): void {
    const hq = this.findStructure("hq", "player");
    const p = hq ? this.worldToScreen(this.structureCenter(hq).x, this.structureCenter(hq).y) : this.worldToScreen(this.playerBaseCell().x, this.playerBaseCell().y);
    this.camera.x = p.x;
    this.camera.y = p.y;
    this.clampCamera();
  }

  private centerOnSelectionOrBase(): void {
    const selected = this.selectedUnits();
    if (!selected.length) {
      this.centerOnPlayerBase();
      return;
    }
    const avg = selected.reduce((acc, unit) => ({ x: acc.x + unit.x, y: acc.y + unit.y }), { x: 0, y: 0 });
    const p = this.worldToScreen(avg.x / selected.length, avg.y / selected.length);
    this.camera.x = p.x;
    this.camera.y = p.y;
    this.clampCamera();
  }

  private clampCamera(): void {
    const corners = [
      this.worldToScreen(-1, -1),
      this.worldToScreen(this.mapSize, -1),
      this.worldToScreen(-1, this.mapSize),
      this.worldToScreen(this.mapSize, this.mapSize)
    ];
    const minX = Math.min(...corners.map((p) => p.x)) - 260;
    const maxX = Math.max(...corners.map((p) => p.x)) + 260;
    const minY = Math.min(...corners.map((p) => p.y)) - 190;
    const maxY = Math.max(...corners.map((p) => p.y)) + 190;
    const halfW = this.width / this.camera.zoom / 2;
    const halfH = this.height / this.camera.zoom / 2;
    this.camera.x = clamp(this.camera.x, minX + halfW, maxX - halfW);
    this.camera.y = clamp(this.camera.y, minY + halfH, maxY - halfH);
  }

  private worldToScreen(x: number, y: number): Vec2 {
    return {
      x: (x - y) * HALF_W,
      y: (x + y) * HALF_H
    };
  }

  private worldToClient(x: number, y: number): Vec2 {
    const p = this.worldToScreen(x, y);
    return {
      x: (p.x - this.camera.x) * this.camera.zoom + this.width / 2,
      y: (p.y - this.camera.y) * this.camera.zoom + this.height / 2
    };
  }

  private clientToWorld(clientX: number, clientY: number): Vec2 {
    return {
      x: (clientX - this.width / 2) / this.camera.zoom + this.camera.x,
      y: (clientY - this.height / 2) / this.camera.zoom + this.camera.y
    };
  }

  private clientToCell(clientX: number, clientY: number): Vec2 {
    const world = this.clientToWorld(clientX, clientY);
    const tx = world.x / HALF_W;
    const ty = world.y / HALF_H;
    return {
      x: clamp(Math.round((ty + tx) / 2), 0, this.mapSize - 1),
      y: clamp(Math.round((ty - tx) / 2), 0, this.mapSize - 1)
    };
  }

  private projectedVisible(p: Vec2, padX: number, padY: number): boolean {
    const x = (p.x - this.camera.x) * this.camera.zoom + this.width / 2;
    const y = (p.y - this.camera.y) * this.camera.zoom + this.height / 2;
    return x > -padX && x < this.width + padX && y > -padY && y < this.height + padY;
  }

  private zoomAt(clientX: number, clientY: number, factor: number): void {
    const before = this.clientToWorld(clientX, clientY);
    const nextZoom = clamp(this.camera.zoom * factor, 0.64, 1.78);
    this.camera.zoom = nextZoom;
    const after = this.clientToWorld(clientX, clientY);
    this.camera.x += before.x - after.x;
    this.camera.y += before.y - after.y;
    this.clampCamera();
  }

  private pathPolygon(points: Vec2[], fill: string, stroke: string): void {
    this.ctx.beginPath();
    this.ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) this.ctx.lineTo(points[i].x, points[i].y);
    this.ctx.closePath();
    this.ctx.fillStyle = fill;
    this.ctx.fill();
    this.ctx.strokeStyle = stroke;
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  private drawRotated(image: HTMLImageElement, x: number, y: number, w: number, h: number, radians: number): void {
    this.ctx.save();
    this.ctx.translate(x, y);
    this.ctx.rotate(radians);
    this.ctx.drawImage(image, -w / 2, -h / 2, w, h);
    this.ctx.restore();
  }

  private drawHealthBar(x: number, y: number, ratio: number, owner: Owner): void {
    const width = 38;
    const height = 5;
    this.ctx.fillStyle = "rgba(0,0,0,0.55)";
    this.ctx.fillRect(x - width / 2, y, width, height);
    this.ctx.fillStyle = ratio > 0.5 ? "#8fe67d" : ratio > 0.25 ? "#f5db8c" : "#e26457";
    if (owner === "enemy" && ratio > 0.5) this.ctx.fillStyle = "#ff8a7e";
    this.ctx.fillRect(x - width / 2, y, width * clamp(ratio, 0, 1), height);
  }

  private drawRadarDish(centerCell: Vec2, color: string): void {
    const p = this.worldToScreen(centerCell.x, centerCell.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 2;
    this.ctx.beginPath();
    this.ctx.arc(p.x, p.y - 58, 16, Math.PI * 1.1, Math.PI * 1.9);
    this.ctx.stroke();
    this.ctx.beginPath();
    this.ctx.moveTo(p.x, p.y - 42);
    this.ctx.lineTo(p.x, p.y - 58);
    this.ctx.stroke();
  }

  private drawFactoryDoor(top: Vec2[], color: string): void {
    const p = {
      x: (top[2].x + top[3].x) / 2,
      y: (top[2].y + top[3].y) / 2 + 18
    };
    this.ctx.fillStyle = "rgba(0,0,0,0.36)";
    this.ctx.fillRect(p.x - 22, p.y - 5, 44, 19);
    this.ctx.strokeStyle = color;
    this.ctx.strokeRect(p.x - 22, p.y - 5, 44, 19);
  }

  private drawRefineryPipes(top: Vec2[], color: string): void {
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 3;
    for (let i = 0; i < 3; i++) {
      const t = 0.25 + i * 0.2;
      const x = lerp(top[0].x, top[1].x, t);
      const y = lerp(top[0].y, top[1].y, t);
      this.ctx.beginPath();
      this.ctx.moveTo(x, y - 18);
      this.ctx.lineTo(x + 5, y + 10);
      this.ctx.stroke();
    }
  }

  private drawPowerStacks(top: Vec2[], color: string): void {
    this.ctx.fillStyle = "#202b2b";
    this.ctx.strokeStyle = color;
    for (let i = 0; i < 2; i++) {
      const x = lerp(top[0].x, top[1].x, 0.35 + i * 0.28);
      const y = lerp(top[0].y, top[1].y, 0.35 + i * 0.28);
      this.ctx.fillRect(x - 5, y - 24, 10, 28);
      this.ctx.strokeRect(x - 5, y - 24, 10, 28);
    }
  }

  private drawTurret(structure: Structure, color: string): void {
    const center = this.structureCenter(structure);
    const p = this.worldToScreen(center.x, center.y);
    const target = this.acquireTarget({ ...center, owner: structure.owner } as Unit, 6);
    let angle = structure.owner === "player" ? -0.6 : 2.4;
    if (target) {
      const tp = this.worldToScreen(this.targetPoint(target).x, this.targetPoint(target).y);
      angle = Math.atan2(tp.y - p.y, tp.x - p.x);
    }
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 5;
    this.ctx.beginPath();
    this.ctx.moveTo(p.x, p.y - 28);
    this.ctx.lineTo(p.x + Math.cos(angle) * 24, p.y - 28 + Math.sin(angle) * 14);
    this.ctx.stroke();
  }

  private tint(hex: string, delta: number): string {
    const n = Number.parseInt(hex.slice(1), 16);
    const r = clamp(((n >> 16) & 255) + delta, 0, 255);
    const g = clamp(((n >> 8) & 255) + delta, 0, 255);
    const b = clamp((n & 255) + delta, 0, 255);
    return `rgb(${r}, ${g}, ${b})`;
  }

  private unitVisualDef(unit: Unit): UnitDef {
    const base = UNIT_DEFS[unit.kind];
    if (unit.owner === "player") return base;
    const skin = ENEMY_SKINS[unit.kind];
    return { ...base, ...skin };
  }

  private toast(message: string): void {
    const el = document.createElement("div");
    el.className = "toast";
    el.textContent = message;
    this.toastLog.prepend(el);
    while (this.toastLog.children.length > 4) this.toastLog.lastElementChild?.remove();
    setTimeout(() => el.remove(), 3600);
  }
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}

new RtsGame();

